import {
	Alert,
	Button,
	Card,
	Col,
	Container,
	Form,
	InputGroup,
	Row,
	Spinner,
} from 'react-bootstrap';
// @ts-ignore
import './OpportunityListPage.scss';
import { useTranslations } from 'src/hooks/useTranslations';
import { useApiClient } from 'src/hooks/useApiClient';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { downloadCSV, downloadXLSX, formatDate } from 'src/utils';
import TableSkeleton from 'src/components/TableSkeleton/TableSkeleton';
import ServerPaginatedTable from 'src/components/ServerPaginatedTable/ServerPaginatedTable';
import i18next from 'i18next';
import { FileExportMenu } from 'src/components/FileExportMenu/FileExportMenu';
import { useErrorToast } from 'src/hooks/useErrorToast';
import Restricted from 'src/components/Restricted/Restricted';
import { RoleEnum } from 'src/api';
import { GTMsendEvent } from 'src/utils/trackingGA4';
import _ from 'lodash';
import Icon from 'src/components/Icon/Icon';
import { Link, useNavigate } from 'react-router-dom';
import { MyOpportunity, SalesforceSelectField } from 'src/api/models/AccountEntity';
import { OPPORTUNITY_SKIP_BACKOFFICE_USERS_INIT_FILTER, OPPORTUNITY_STATES_DATA } from 'src/utils/constants';
import Select from 'react-select';
import { translateOpportunityState } from 'src/utils/translations';
import { useAuth } from 'src/hooks/useAuth';
import { SortingRule } from 'react-table';
import { MdOutlineNotificationsActive } from 'react-icons/md';
import { ReactSelectOption } from 'src/types';

type FilterQuery = {
	orderDate?: string;
	accountName?: string;
	name?: string;
	dealer?: string;
	salesUser?: string;
	referenceUser?: string;
	noleggiatore?: string;
	stageName?: string;
	dataInvioAlCreditoFrom?: string;
	dataInvioAlCreditoTo?: string;
	limit: number;
	canale?: string;
	dataCreazioneFrom?: string;
	dataCreazioneTo?: string;
};

const INIT_FILTER_QUERY = {
	orderDate: undefined,
	accountName: '',
	name: '',
	dealer: '',
	salesUser: '',
	referenceUser: '',
	noleggiatore: '',
	stageName: '',
	dataInvioAlCreditoFrom: '',
	dataInvioAlCreditoTo: '',
	canale: '',
	orderBy: undefined,
	dataCreazioneFrom: '',
	dataCreazioneTo: '',
	limit: 50,
} as const;

const DEFAULT_PAGINATION: { skip: number; limit: number } = {
	skip: 0,
	limit: 50,
} as const;

const BATCH_SIZE: number = 1000;

const NotificationIcon = ({ title }) => {
	const [isAnimating, setIsAnimating] = useState(false);

	useEffect(() => {
		setIsAnimating(true);
		const timer = setTimeout(() => {
			setIsAnimating(false);
		}, 2500);

		return () => clearTimeout(timer);
	}, []);

	return (
		<div className="notification-icon">
			<MdOutlineNotificationsActive
				color="var(--cyan-color)"
				size={24}
				title={title}
				className={`icon ${isAnimating ? 'animate' : ''}`}
			/>
		</div>
	);
};

const OpportunityListPage = () => {
	const { t } = useTranslations();
	const { apiClient, handleApi } = useApiClient();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [response, setResponse] = useState<any>(null);
	const { showError } = useErrorToast();
	const { user, userRole } = useAuth();
	const [filterQuery, setFilterQuery] = useState<FilterQuery>(Object.assign({},
		INIT_FILTER_QUERY,
		userRole === RoleEnum.BACKOFFICE && !OPPORTUNITY_SKIP_BACKOFFICE_USERS_INIT_FILTER.includes(user?.username?.toLowerCase())
    ? { referenceUser: user?.username }
    : {}
	));
	const [tableIsBusy, setTableIsBusy] = useState<boolean>(false);
	const navigate = useNavigate();
	const [accountFields, setAccountFields] = useState<SalesforceSelectField[]>([]);

	const dealerOptions = useMemo<ReactSelectOption[]>(() => {
		if (!accountFields || !accountFields.length) return [];
		const options = accountFields.find(field => field?.name?.toLowerCase() === 'filiale__c')?.picklistValues;
		return options || [];
	}, [accountFields])

	const stageOptions = useMemo(() => {
		return Object.keys(OPPORTUNITY_STATES_DATA)
			.sort(
				(x, y) =>
					(OPPORTUNITY_STATES_DATA[x]?.position || 0) -
					(OPPORTUNITY_STATES_DATA[y]?.position || 0)
			)
			.map((key) => ({
				value: key,
				label:
					translateOpportunityState(OPPORTUNITY_STATES_DATA[key]?.label) || key,
			}));
	}, []);

	const selectedStageOption = useMemo(() => {
		if (_.isEmpty(filterQuery.stageName)) {
			return null;
		}

		return (stageOptions || []).find(
			(item) => filterQuery.stageName === item?.value
		);
	}, [filterQuery.stageName, stageOptions]);

	const selectedDealerOption = useMemo(() => {
		if (_.isEmpty(filterQuery.dealer)) {
			return null;
		}

		return (dealerOptions || []).find(
			(item) => filterQuery.dealer === item?.value
		);
	}, [filterQuery.dealer, dealerOptions]);

	const tableColumns = useMemo(() => {
		return [
			{
				accessor: 'Alert',
				id: 'alert',
				Header: '',
				disableSortBy: true,
				Cell: ({ row }) => {
					return ![
						RoleEnum.SALES,
						RoleEnum.SALES_DEALER,
						RoleEnum.SALES_INTERNAL,
					].includes(userRole) && row.original.Alert ? (
						<NotificationIcon
							title={i18next.t(row.original.Alert)}
							key={row.original.Id}
						/>
					) : null;
				},
			},
			{
				accessor: 'accountName',
				id: 'account_name',
				Header: i18next.t('account_name'),
				width: 180,
				disableSortBy: true,
			},
			{
				accessor: 'Name',
				id: 'opportunity_name',
				Header: i18next.t('opportunity_name'),
				width: 180,
				disableSortBy: true,
			},
			{
				accessor: 'CreatedDate',
				id: 'CreatedDate',
				Header: i18next.t('date_creation'),
				inputType: 'date',
				width: 200,
				Cell: ({ row }) => {
					const rawDate = row?.original?.CreatedDate;
					const formatted = formatDate(rawDate, 'it-IT', false);
					return formatted === 'Invalid Date' ? rawDate : formatted;
				},
			},
			{
				accessor: 'Data_invio_al_credito__c',
				id: 'Data_invio_al_credito__c',
				Header: i18next.t('credit_score_check_date'),
				inputType: 'date',
				width: 200,
				Cell: ({ row }) => {
					const rawDate = row?.original?.Data_invio_al_credito__c;
					if (!rawDate) return null;
					const formatted = formatDate(rawDate, 'it-IT', false);
					return formatted === 'Invalid Date' ? rawDate : formatted;
				},
				disableSortBy: false,
			},
			{
				accessor: 'Nome_partner__c',
				id: 'dealer',
				Header: i18next.t('dealer'),
				width: 180,
				disableSortBy: true,
			},
			{
				accessor: 'Owner_HMP__c',
				id: 'Owner_HMP__c',
				Header: i18next.t('sales'),
				width: 180,
				disableSortBy: true,
			},
			{
				accessor: 'Owner.Email',
				id: 'reference_user',
				Header: i18next.t('manager'),
				width: 180,
				disableSortBy: true,
			},
			{
				accessor: 'Noleggiatore__c',
				id: 'noleggiatore',
				Header: i18next.t('rental_company'),
				width: 180,
				disableSortBy: true,
			},
			{
				accessor: 'Canale__c',
				id: 'canale',
				Header: i18next.t('channel'),
				width: 180,
				disableSortBy: true,
			},
			{
				accessor: 'StageName',
				id: 'stato_pratica',
				Header: i18next.t('stato_pratica'),
				width: 180,
				disableSortBy: true,
				Cell: ({ value }) => {
					return (
						translateOpportunityState(OPPORTUNITY_STATES_DATA[value]?.label) ||
						value
					);
				},
			},
			{
				accessor: 'Marca__c',
				id: 'marca',
				Header: i18next.t('brand'),
				width: 180,
				disableSortBy: true,
				Cell: ({ row }) => {
					return row?.original?.Marca__c || row?.original?.Brand__c;
				},
			},
			{
				accessor: 'Modello__c',
				id: 'modello',
				Header: i18next.t('model'),
				width: 180,
				disableSortBy: true,
			},
			{
				width: 60,
				accessor: 'actions',
				id: 'actions',
				Header: '',
				disableSortBy: true,
				headerClassName: 'column-sticky',
				className: 'column-sticky text-end',
				Cell: ({ row }) => {
					return (
						<Link
							to={`/opportunities/${row.original.Id}?from=my-opportunities`}
							className="btn"
						>
							<Icon
								style={{ width: '1.2rem', height: '1.2rem' }}
								name="arrow-right"
							/>
						</Link>
					);
				},
			},
		];
	}, []);

	useEffect(() => {
		setError(null);
		handleApi(
			apiClient.salesforceOpportunityController.getMyOpportunities({
				...DEFAULT_PAGINATION,
				...computeQueryParams(filterQuery)
			})
		)
			.then((res) => {
				setResponse(res);
			})
			.catch((err) => {
				setError(err);
				showError(err, i18next.t('error_loading_data'));
			})
			.finally(() => {
				setLoading(false);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fetchAccountFields = async () => {
		try {
			const apiResponse = await handleApi(apiClient.salesforceAccountController.getAccountFields());
			setAccountFields(apiResponse);
		} catch (error) {
			console.warn(error);
		}
	}

	useEffect(() => {
		fetchAccountFields();
	}, []);

	const computeQueryParams = useCallback((queryParams) => {
		const dataInvioAlCreditoFrom = queryParams.dataInvioAlCreditoFrom
			? new Date(queryParams.dataInvioAlCreditoFrom).toISOString()
			: undefined;
		const dataInvioAlCreditoTo = queryParams.dataInvioAlCreditoTo
			? new Date(queryParams.dataInvioAlCreditoTo).toISOString()
			: undefined;

		const dataCreazioneFrom = queryParams.dataCreazioneFrom
			? new Date(queryParams.dataCreazioneFrom).toISOString()
			: undefined;
		const dataCreazioneTo = queryParams.dataCreazioneTo
			? new Date(queryParams.dataCreazioneTo).toISOString()
			: undefined;

		const params = Object.assign({}, queryParams, {
			dataInvioAlCreditoFrom,
			dataInvioAlCreditoTo,
			dataCreazioneFrom,
			dataCreazioneTo,
		});

		return _.omitBy(
			params,
			(value: any) => _.isNil(value) || (_.isString(value) && _.isEmpty(value))
		);
	}, []);

	const refreshTable = useCallback(
		async (queryParams: FilterQuery, opts = { ...DEFAULT_PAGINATION }) => {
			setTableIsBusy(true);
			handleApi(
				apiClient.salesforceOpportunityController.getMyOpportunities({
					...(computeQueryParams(queryParams) || {}),
					...opts,
				})
			)
				.then((res) => {
					setResponse(res);
					setError(null);
				})
				.catch((err) => {
					showError(err, t('fetch_my_opportunities_error'));
				})
				.finally(() => {
					setTableIsBusy(false);
				});
			// eslint-disable-next-line react-hooks/exhaustive-deps
		},
		[loading, showError, t, tableIsBusy]
	);

	const data = useMemo(() => {
		const items = response?.data || [];

		if (
			[RoleEnum.SALES, RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL].includes(
				userRole
			)
		) {
			return items;
		}

		return items.sort((x, y) => {
			const hasAlertX = !_.isEmpty(x?.Alert);
			const hasAlertY = !_.isEmpty(y?.Alert);

			if (hasAlertX && !hasAlertY) return -1;
			if (!hasAlertX && hasAlertY) return 1;
			return 0;
		});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [response?.data]);

	const fetchAllOpportunities = async (
		apiCall: (params: any) => Promise<any>,
		queryParams: Record<string, any> = {},
		batchSize: number = 100
	): Promise<MyOpportunity[]> => {
		const allData: MyOpportunity[] = [];
		let skip = 0;
		let totalCount = 0;

		do {
			try {
				const response = await apiCall({
					...queryParams,
					skip,
					limit: batchSize,
				});

				allData.push(...response.data);
				totalCount = response.totalCount;
				skip += batchSize;

				if (allData.length >= totalCount || response.data.length === 0) {
					break;
				}
			} catch (error) {
				console.error(`Error fetching batch starting at ${skip}:`, error);
				throw error;
			}
		} while (allData.length < totalCount);

		return allData;
	};

	const handleExportButton = useCallback(
		async (format) => {
			let dataToExport = data;

			if (response?.totalCount > 2000) {
				const errMessage = i18next.t('opportunity_export_limit_reached');
				showError(new Error(errMessage), errMessage);
				return;
			}

			if (response?.totalCount > data?.length) {
				try {
					setLoading(true);
					const apiCallWrapper = (params: any) =>
						handleApi(
							apiClient.salesforceOpportunityController.getMyOpportunities(
								params
							)
						);

					const items = await fetchAllOpportunities(
						apiCallWrapper,
						computeQueryParams(filterQuery) || {},
						BATCH_SIZE
					);

					dataToExport = items || [];
				} catch (err) {
					showError(err, i18next.t('error_fetching_all_data_for_export'));
					setLoading(false);
					return;
				} finally {
					setLoading(false);
				}
			}

			if (!dataToExport.length) {
				showError(null, i18next.t('no_data_available_for_download'));
				return;
			}

			const exportableColumns = tableColumns.filter(
				(col) => !['checkboxes', 'id', 'actions'].includes(col.id)
			);

			const csvData = [];

			// @ts-ignore
			csvData.push(exportableColumns.map((col) => col.Header));

			dataToExport.forEach((rowOriginal) => {
				const rowValues = exportableColumns.map((col) => {
					let value = rowOriginal[col.accessor];

					if (col.id === 'credit_score_check_date') {
						const rawDate = rowOriginal?.credit_score_check_date;
						const formatted = formatDate(rawDate, 'it-IT', false);
						value = formatted === 'Invalid Date' ? rawDate : formatted;
					}

					if (col.id === 'reference_user') {
						value = rowOriginal?.Owner?.Email;
					}

					if (typeof value === 'string') {
						value = value.replace(/\n/g, ' ');
					}

					return value || '';
				});
				// @ts-ignore
				csvData.push(rowValues);
			});

			console.log('Exporting data in format:', format, csvData);

			let fileName = [i18next.t('my_opportunities_list')]
				.join('_')
				.replace(' ', '_');

			switch (format) {
				case 'xlsx':
					downloadXLSX(csvData, fileName);
					break;
				case 'csv':
					downloadCSV(csvData, fileName);
					break;
				default:
					console.warn(`[Horizon] Unsupported export format: ${format}`);
					break;
			}

			GTMsendEvent({
				event: 'CTA_download_file',
				evtCat: 'lista_opportunities',
				evtAct: 'download',
				evtLab: `${fileName}.${format}`,
			});
		},
		[
			data,
			tableColumns,
			handleApi,
			apiClient.salesforceOpportunityController,
			computeQueryParams,
			filterQuery,
			response,
			showError,
		]
	);

	const handleResetFilter = useCallback(() => {
		const filter = Object.assign({}, INIT_FILTER_QUERY);
		setFilterQuery(filter);
		refreshTable(filter);
	}, [refreshTable]);

	const handleSortChange = (rules: SortingRule<Object>[]) => {
		const sortBy = rules?.[0]?.id;
		const isDesc = rules?.[0]?.desc;

		const newFilter = {
			...filterQuery,
			orderBy: sortBy,
			orderDate: sortBy ? (isDesc ? 'desc' : 'asc') : undefined,
		};

		if (_.isEqual(filterQuery, newFilter)) {
			return;
		}

		setFilterQuery(newFilter);
		refreshTable(newFilter);
	};

	return (
		<Container fluid>
			<Restricted
				to={[
					RoleEnum.MASTER,
					RoleEnum.BACKOFFICE,
					RoleEnum.SALES,
					RoleEnum.SALES_DEALER,
					RoleEnum.SALES_INTERNAL,
					RoleEnum.ADMIN,
					RoleEnum.AREA_MANAGER,
				]}
			>
				<Row className="mb-4">
					<Col md={6} className="display-6">
						{t('my_opportunities_list')}
					</Col>
					<Col md={6} className="mt-4 mt-md-0 text-md-end">
						<FileExportMenu
							isDisabled={loading}
							allowedFormats={['csv', 'xlsx']}
							handleExport={handleExportButton}
						/>
					</Col>
				</Row>
				<Row>
					<Col>
						<Card>
							<Card.Body>
								<Form
									onSubmit={(e) => {
										e.preventDefault();
										refreshTable(filterQuery);
									}}
								>
									<Row className="mb-3">
										<Col md={4}>
											<Form.Group>
												<Form.Label>{t('account_name')}</Form.Label>
												<Form.Control
													onChange={({ target }) => {
														setFilterQuery({
															...filterQuery,
															accountName: target.value,
														});
													}}
													placeholder={t('account_name')}
													type="search"
													value={filterQuery.accountName}
													name="accountName"
												/>
											</Form.Group>
										</Col>
										<Col md={4}>
											<Form.Group>
												<Form.Label>{t('opportunity_name')}</Form.Label>
												<Form.Control
													onChange={({ target }) => {
														setFilterQuery({
															...filterQuery,
															name: target.value,
														});
													}}
													placeholder={t('opportunity_name')}
													type="search"
													value={filterQuery.name}
													name="name"
												/>
											</Form.Group>
										</Col>
										<Col md={4}>
											<Form.Group>
												<Form.Label>{t('dealer')}</Form.Label>
												<Select
													menuPosition="fixed"
													value={selectedDealerOption}
													name="dealer"
													isClearable
													placeholder={t('dealer')}
													options={dealerOptions as any}
													onChange={(item: any) => {
														setFilterQuery((prev) => ({
															...prev,
															...{ dealer: item?.value },
														}));
													}}
												/>
											</Form.Group>
										</Col>
									</Row>
									<Row className="mb-3">
										<Col md={4}>
											<Form.Group>
												<Form.Label>{t('sales')}</Form.Label>
												<Form.Control
													onChange={({ target }) => {
														setFilterQuery({
															...filterQuery,
															salesUser: target.value,
														});
													}}
													placeholder={t('sales')}
													type="search"
													value={filterQuery.salesUser}
													name="salesUser"
												/>
											</Form.Group>
										</Col>
										<Col md={4}>
											<Form.Group>
												<Form.Label>{t('manager')}</Form.Label>
												<Form.Control
													onChange={({ target }) => {
														setFilterQuery({
															...filterQuery,
															referenceUser: target.value,
														});
													}}
													placeholder={t('manager')}
													type="search"
													value={filterQuery.referenceUser}
													name="referenceUser"
												/>
											</Form.Group>
										</Col>
										<Col md={4}>
											<Form.Group>
												<Form.Label>{t('rental_company')}</Form.Label>
												<Form.Control
													onChange={({ target }) => {
														setFilterQuery({
															...filterQuery,
															noleggiatore: target.value,
														});
													}}
													placeholder={t('rental_company')}
													type="search"
													value={filterQuery.noleggiatore}
													name="noleggiatore"
												/>
											</Form.Group>
										</Col>
									</Row>
									<Row className="mb-3">
										<Col md={4}>
											<Form.Group>
												<Form.Label>{t('stato_pratica')}</Form.Label>
												<Select
													menuPosition="fixed"
													value={selectedStageOption}
													name="stageName"
													isClearable
													placeholder={t('stato_pratica')}
													options={stageOptions as any}
													onChange={(item: any) => {
														setFilterQuery((prev) => ({
															...prev,
															...{ stageName: item?.value },
														}));
													}}
												/>
											</Form.Group>
										</Col>
										<Col>
											<Form.Label>{t('credit_score_check_date')}</Form.Label>
											<InputGroup>
												<Form.Control
													name="from"
													size="lg"
													type="date"
													placeholder={`${i18next.t('from')}...`}
													value={filterQuery.dataInvioAlCreditoFrom}
													onChange={(e) => {
														setFilterQuery((prev) => ({
															...prev,
															...{ dataInvioAlCreditoFrom: e.target.value },
														}));
													}}
												/>
												<Form.Control
													name="to"
													size="lg"
													type="date"
													placeholder={`${i18next.t('to')}...`}
													value={filterQuery.dataInvioAlCreditoTo}
													onChange={(e) => {
														setFilterQuery((prev) => ({
															...prev,
															...{ dataInvioAlCreditoTo: e.target.value },
														}));
													}}
												/>
											</InputGroup>
										</Col>
									</Row>
									<Row className="mb-3">
										<Col md={4}>
											<Form.Group>
												<Form.Label>{t('channel')}</Form.Label>
												<Form.Control
													onChange={({ target }) => {
														setFilterQuery({
															...filterQuery,
															canale: target.value,
														});
													}}
													placeholder={t('channel')}
													type="search"
													value={filterQuery.canale}
													name="canale"
												/>
											</Form.Group>
										</Col>
										<Col>
											<Form.Label>{t('date_creation')}</Form.Label>
											<InputGroup>
												<Form.Control
													name="from"
													size="lg"
													type="date"
													placeholder={`${i18next.t('from')}...`}
													value={filterQuery.dataCreazioneFrom}
													onChange={(e) => {
														setFilterQuery((prev) => ({
															...prev,
															...{ dataCreazioneFrom: e.target.value },
														}));
													}}
												/>
												<Form.Control
													name="to"
													size="lg"
													type="date"
													placeholder={`${i18next.t('to')}...`}
													value={filterQuery.dataCreazioneTo}
													onChange={(e) => {
														setFilterQuery((prev) => ({
															...prev,
															...{ dataCreazioneTo: e.target.value },
														}));
													}}
												/>
											</InputGroup>
										</Col>
									</Row>
									<Row className="align-items-end">
										<Col className="text-end">
											<Button
												disabled={tableIsBusy}
												variant="secondary"
												onClick={handleResetFilter}
												className="me-2"
											>
												{t('reset')}
											</Button>
											<Button disabled={tableIsBusy} type="submit">
												{tableIsBusy ? (
													<Spinner animation="border" size="sm" />
												) : (
													t('search')
												)}
											</Button>
										</Col>
									</Row>
								</Form>
							</Card.Body>
						</Card>
					</Col>
				</Row>
				{!loading && response ? (
					<div>
						{data?.length ? (
							<ServerPaginatedTable
								pageSize={filterQuery.limit}
								onPageSizeChange={(newPageSize) => {
									const newFilter = {
										...filterQuery,
										skip: 0,
										limit: newPageSize,
									};
									setFilterQuery((prev) => ({ ...prev, ...newFilter }));
									refreshTable(newFilter, { skip: 0, limit: newPageSize });
								}}
								showPageSelector
								totalCount={response?.totalCount || 0}
								columns={tableColumns}
								onSortChange={handleSortChange}
								loadFn={(skip, limit) => {
									refreshTable(filterQuery, { skip, limit });
								}}
								error={error}
								data={data}
								loading={loading}
								dblClickCallback={(row) => {
									if (!row.Id) {
										console.warn('no row ID:', row);
										return;
									}
									navigate(`/opportunities/${row.Id}?from=my-opportunities`);
								}}
							></ServerPaginatedTable>
						) : (
							<div className="mt-2">
								<Alert>{t('no_opportunity_found')}</Alert>
							</div>
						)}
					</div>
				) : (
					<TableSkeleton />
				)}
			</Restricted>
		</Container>
	);
};

export default OpportunityListPage;
